import { z } from 'zod';
import { studioChoiceSchema, studioOf, type StudioChoice } from './choice.ts';

/**
 * Meus modelos (ADR 0133, parte 5): escolhas do Estúdio guardadas com um nome, para abrir de
 * novo, comparar e levar para outro sistema num arquivo. Guardam a escolha, nunca o tema.
 */
export const STYLE_MODELS_MAX = 20;

export const styleModelSchema = z.object({
  id: z.string().regex(/^[a-z0-9-]{1,40}$/),
  name: z.string().trim().min(1).max(60),
  studio: studioChoiceSchema,
  savedAt: z.iso.datetime(),
});
export type StyleModel = z.infer<typeof styleModelSchema>;

/** A escolha que esta base desenha: todos os ids existem. */
export function isKnownStudio(s: StudioChoice): boolean {
  return studioOf({ style: 'clear', studio: s }) === s;
}

export const styleModelsInput = z.object({
  models: z
    .array(styleModelSchema.refine((m) => isKnownStudio(m.studio), 'Estilo que não existe'))
    .max(STYLE_MODELS_MAX)
    .refine((m) => new Set(m.map((x) => x.id)).size === m.length, 'Modelo repetido'),
});

/** Os modelos gravados que a base ainda entende (um que ficou com id que saiu da lista some). */
export function readStyleModels(value: unknown): StyleModel[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((v) => {
    const m = styleModelSchema.safeParse(v);
    return m.success && isKnownStudio(m.data.studio) ? [m.data] : [];
  });
}

/** O arquivo de um estilo, para levar de um sistema a outro. */
export const STYLE_FILE_KIND = 'base-style';

const styleFileSchema = z.object({
  kind: z.literal(STYLE_FILE_KIND),
  version: z.literal(1),
  name: z.string().trim().min(1).max(60),
  studio: studioChoiceSchema,
});

export function styleFileOf(name: string, studio: StudioChoice): string {
  return `${JSON.stringify({ kind: STYLE_FILE_KIND, version: 1, name, studio }, null, 2)}\n`;
}

/** Lê o arquivo de um estilo: a escolha, se o formato confere e os ids existem nesta base. */
export function parseStyleFile(
  text: string,
): { ok: true; name: string; studio: StudioChoice } | { ok: false; reason: 'json' | 'format' } {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return { ok: false, reason: 'json' };
  }
  const r = styleFileSchema.safeParse(raw);
  if (!r.success || !isKnownStudio(r.data.studio)) return { ok: false, reason: 'format' };
  return { ok: true, name: r.data.name, studio: r.data.studio };
}
